import { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Building2, Loader2, ChevronRight, Calendar, X } from 'lucide-react';
import { SelectOrgPrompt, useNeedsOrg } from '../components/SelectOrgPrompt';
import { useAuth } from '../contexts/AuthContext';
import ProviderDashboard from '../components/ProviderDashboard';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import CompanyDashboard from '../components/CompanyDashboard';
import WelcomeModal from '../components/WelcomeModal';
import axios from 'axios';

import { API_BASE as API } from '../config';

// Animated wrapper for smooth solo <-> provider dashboard transitions
const DashboardTransition = ({ viewKey, children }) => {
  const [phase, setPhase] = useState('enter'); // 'enter' | 'visible'
  const prevKey = useRef(viewKey);

  useEffect(() => {
    if (prevKey.current !== viewKey) {
      setPhase('enter');
      prevKey.current = viewKey;
    }
    const raf = requestAnimationFrame(() => setPhase('visible'));
    return () => cancelAnimationFrame(raf);
  }, [viewKey]);

  return (
    <div
      className="transition-all duration-500 ease-out"
      style={{
        opacity: phase === 'visible' ? 1 : 0,
        transform: phase === 'visible' ? 'translateY(0)' : 'translateY(12px)',
      }}
    >
      {children}
    </div>
  );
};

// Color palettes for each chart
const COMPANY_COLORS = ['#38bdf8', '#f472b6', '#e11d55', '#f59e0b', '#8b5cf6', '#10b981', '#f97316', '#06b6d4'];
const STATUS_COLORS_MAP = { 'New': '#3b82f6', 'In Process': '#f59e0b', 'Closed': '#34d399' };
const FAULT_COLORS_MAP = { 'At Fault': '#e11d55', 'Not At Fault': '#14b8a6', 'Unknown': '#64748b' };

// Add percent field to data for LabelList
const addPercent = (data) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  return data.map(d => ({ ...d, pct: total > 0 ? `${((d.value / total) * 100).toFixed(1)}%` : '' }));
};

const OTHER_COLOR = '#94a3b8';
const MAX_LEGEND = 3;

const prepareChartData = (data, colorMap, colorArray) => {
  const filtered = data.filter(d => d.value > 0);
  if (filtered.length <= MAX_LEGEND) return { chartData: filtered, topItems: filtered, otherItems: [] };
  const sorted = [...filtered].sort((a, b) => b.value - a.value);
  const top = sorted.slice(0, MAX_LEGEND);
  const rest = sorted.slice(MAX_LEGEND);
  // Pie shows ALL slices individually; legend groups rest as "Other"
  return { chartData: sorted, topItems: top, otherItems: rest };
};

const DashboardPie = ({ title, data, colorMap, colorArray }) => {
  const hasData = data && data.some(d => d.value > 0);
  const { chartData, topItems, otherItems } = hasData
    ? prepareChartData(data, colorMap, colorArray)
    : { chartData: [], topItems: [], otherItems: [] };
  const [showOtherTooltip, setShowOtherTooltip] = useState(false);
  const otherRef = useRef(null);

  const getColor = (entry, i) => {
    return colorMap ? (colorMap[entry.name] || colorArray?.[i % (colorArray?.length || 1)] || OTHER_COLOR) : (colorArray[i % colorArray.length]);
  };

  const pieData = addPercent(chartData);

  return (
    <Card className="border-none shadow-none bg-transparent" data-testid={`pie-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start gap-8 mb-2 min-h-[76px]">
          <h3 className="text-base font-semibold text-gray-800 shrink-0">{title}</h3>
          {hasData && (
            <div className="flex flex-col gap-1.5 mt-6">
              {topItems.map((entry, i) => (
                <div key={entry.name} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: getColor(entry, i) }} />
                  <span className="text-xs text-gray-600">{entry.name}</span>
                </div>
              ))}
              {otherItems.length > 0 && (
                <div
                  ref={otherRef}
                  className="flex items-center gap-2 cursor-pointer relative"
                  onMouseEnter={() => setShowOtherTooltip(true)}
                  onMouseLeave={() => setShowOtherTooltip(false)}
                >
                  <span className="text-xs text-gray-500">Other ({otherItems.length})</span>
                  {showOtherTooltip && (
                    <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[180px]">
                      {otherItems.map((item, i) => (
                        <div key={item.name} className="flex items-center justify-between gap-3 py-0.5">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: getColor(item, MAX_LEGEND + i) }} />
                            <span className="text-xs text-gray-700">{item.name}</span>
                          </div>
                          <span className="text-xs font-medium text-gray-500">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {!hasData ? (
          <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">No data</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={pieData}
                cx="40%"
                cy="50%"
                outerRadius={110}
                dataKey="value"
                labelLine={false}
                strokeWidth={2}
                stroke="#fff"
                isAnimationActive={false}
              >
                {pieData.map((entry, i) => (
                  <Cell
                    key={`${entry.name}-${i}`}
                    fill={getColor(entry, i)}
                  />
                ))}
                <LabelList dataKey="pct" position="inside" fill="#fff" fontSize={13} fontWeight={700} stroke="none" />
              </Pie>
              <Tooltip formatter={(value) => value} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Loss type bar colors matching stat card order
const LOSS_TYPE_BAR = [
  { key: 'al_count', abbr: 'AL', color: '#e11d55' },   // crimson red (matching pie)
  { key: 'pd_count', abbr: 'PD', color: '#f59e0b' },   // orange (matching pie)
  { key: 'cd_count', abbr: 'CD', color: '#3b82f6' },   // blue
  { key: 'ocac_count', abbr: 'OCAC', color: '#8b5cf6' }, // violet (matching pie)
  { key: 'wc_count', abbr: 'WC', color: '#22c55e' },   // green
];

const LossTypeBar = ({ stats }) => {
  const total = stats?.total_claims ?? 0;
  const segments = total ? LOSS_TYPE_BAR.map(t => ({ ...t, value: stats?.[t.key] ?? 0 })).filter(s => s.value > 0) : [];
  const rawPcts = segments.map(s => (s.value / total) * 100);
  const MIN_PCT = 8;
  const extraNeeded = rawPcts.reduce((sum, p) => sum + (p < MIN_PCT ? MIN_PCT - p : 0), 0);
  const largeTotal = rawPcts.reduce((sum, p) => sum + (p >= MIN_PCT ? p : 0), 0);
  const displayPcts = rawPcts.map(p => p < MIN_PCT ? MIN_PCT : p - (extraNeeded * (p / largeTotal)));

  return (
    <div className="mb-6 md:mb-8" data-testid="loss-type-bar">
      {!total ? (
        <>
          <div className="hidden md:flex h-8 rounded-lg overflow-hidden bg-gray-100">
            <div className="h-full w-full flex items-center justify-center text-xs text-gray-400">Claims By Loss Type</div>
          </div>
          <div className="md:hidden h-8 rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-400">Claims By Loss Type</div>
        </>
      ) : (
        <>
      {/* Desktop: single stacked bar */}
      <div className="hidden md:flex h-8 rounded-lg overflow-hidden">
        {segments.map((s, i) => (
          <div
            key={s.key}
            className="h-full flex items-center justify-center transition-all min-w-0"
            style={{ width: `${displayPcts[i]}%`, backgroundColor: s.color }}
          >
            <span className="text-xs font-semibold text-white whitespace-nowrap" style={{ textShadow: '0 0 3px rgba(0,0,0,0.3)' }}>
              {s.abbr} {Math.round(rawPcts[i])}%
            </span>
          </div>
        ))}
      </div>
      {/* Mobile: multi-bar horizontal chart */}
      <div className="md:hidden space-y-2">
        {segments.map((s, i) => (
          <div key={s.key} className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-600 w-10 shrink-0">{s.abbr}</span>
            <div className="flex-1 bg-gray-200 rounded-full h-5 overflow-hidden">
              <div
                className="h-full rounded-full flex items-center justify-end pr-2 transition-all"
                style={{ width: `${Math.max(rawPcts[i], 8)}%`, backgroundColor: s.color }}
              >
                <span className="text-[10px] font-bold text-white" style={{ textShadow: '0 0 2px rgba(0,0,0,0.3)' }}>
                  {Math.round(rawPcts[i])}%
                </span>
              </div>
            </div>
            <span className="text-xs text-gray-500 w-5 text-right shrink-0">{s.value}</span>
          </div>
        ))}
      </div>
        </>
      )}
    </div>
  );
};

const DATE_PRESETS = [
  { label: 'All Time', value: 'all' },
  { label: 'This Month', value: 'this_month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'This Year', value: 'this_year' },
  { label: 'Last Year', value: 'last_year' },
  { label: 'Custom', value: 'custom' },
];

const getDateRange = (preset) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  switch (preset) {
    case 'this_month': return { start: `${y}-${pad(m+1)}-01`, end: fmt(now) };
    case 'last_month': {
      const lm = m === 0 ? 11 : m - 1;
      const ly = m === 0 ? y - 1 : y;
      const last = new Date(ly, lm + 1, 0);
      return { start: `${ly}-${pad(lm+1)}-01`, end: fmt(last) };
    }
    case 'this_year': return { start: `${y}-01-01`, end: fmt(now) };
    case 'last_year': return { start: `${y-1}-01-01`, end: `${y-1}-12-31` };
    default: return {};
  }
};

const Home = () => {
  const { user, selectedTenants, allTenants, activeOrgId, setActiveOrgId, operatorContext, setOperatorContext, userOrgs } = useAuth();
  const needsOrg = useNeedsOrg();
  const isOperatorInContext = user?.is_platform_operator && operatorContext?.tenantId;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState('all');
  const [prevPreset, setPrevPreset] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [selectedCompanyDashboard, setSelectedCompanyDashboard] = useState(null);
  const dropdownRef = useRef(null);
  const companyDropdownRef = useRef(null);
  const companyCloseTimer = useRef(null);
  const dateCloseTimer = useRef(null);

  // Close company dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target)) setCompanyModalOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (needsOrg && !isOperatorInContext) { setLoading(false); return; }
    setLoading(true);
    const params = {};
    if (datePreset === 'custom') {
      if (customStart) params.start_date = customStart;
      if (customEnd) params.end_date = customEnd;
    } else if (datePreset !== 'all') {
      const range = getDateRange(datePreset);
      params.start_date = range.start;
      params.end_date = range.end;
    }
    axios.get(`${API}/dashboard/stats`, { params, withCredentials: true })
      .then((res) => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [selectedTenants, needsOrg, datePreset, customStart, customEnd, activeOrgId, operatorContext?.tenantId, operatorContext?.orgId]);

  // Close dropdown on outside click — revert custom if no dates
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        if (datePreset === 'custom' && !customStart && !customEnd) {
          setDatePreset(prevPreset);
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [datePreset, customStart, customEnd, prevPreset]);

  // Fetch company list when modal opens or company dashboard is shown
  useEffect(() => {
    if (companyModalOpen || selectedCompanyDashboard) {
      axios.get(`${API}/companies`, { withCredentials: true })
        .then(res => setCompanyList(res.data || []))
        .catch(() => setCompanyList([]));
    }
  }, [companyModalOpen, selectedCompanyDashboard]);

  if (needsOrg && !isOperatorInContext) return <SelectOrgPrompt />;
  // Show Provider Dashboard when provider has no org selected (viewing "All Orgs")
  const isProviderNoOrg = user?.account_type === 'provider' && !activeOrgId;
  const isOperatorProviderNoOrg = isOperatorInContext && operatorContext?.accountType === 'provider' && !operatorContext?.orgId;
  if (isProviderNoOrg || isOperatorProviderNoOrg) {
    return <DashboardTransition viewKey="provider"><WelcomeModal /><ProviderDashboard onSelectOrg={(orgId) => {
      if (isOperatorProviderNoOrg) {
        // Find org name from the provider's org list and update operator context
        axios.get(`${API}/operator/tenants/${operatorContext.tenantId}/organizations`, { withCredentials: true })
          .then(res => {
            const org = res.data.find(o => o.org_id === orgId);
            if (org) {
              setOperatorContext({ ...operatorContext, orgId: org.org_id, orgName: org.name });
            }
          });
      } else {
        setActiveOrgId(orgId);
      }
    }} /></DashboardTransition>;
  }
  if (loading) return <div className="min-h-screen bg-gray-200 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  // Show Company Dashboard when a company is selected
  if (selectedCompanyDashboard) {
    return (
      <div className="min-h-screen bg-gray-200 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <CompanyDashboard
            companyName={selectedCompanyDashboard}
            onBack={() => setSelectedCompanyDashboard(null)}
            companies={companyList}
            onSelectCompany={(name) => setSelectedCompanyDashboard(name)}
          />
        </div>
      </div>
    );
  }

  const cards = [
    { title: 'Total Claims', value: stats?.total_claims ?? 0 },
    { title: 'Auto Liability', value: stats?.al_count ?? 0 },
    { title: 'Physical Damage', value: stats?.pd_count ?? 0 },
    { title: 'Cargo Damage', value: stats?.cd_count ?? 0 },
    { title: 'Occupational Accident', value: stats?.ocac_count ?? 0 },
    { title: 'Workers Compensation', value: stats?.wc_count ?? 0 },
  ];

  const dashboardTitle = (() => {
    if (user?.role === 'super_admin' && selectedTenants.length === 1) {
      const t = allTenants.find((t) => t.tenant_id === selectedTenants[0]);
      return t ? `${t.name} Dashboard` : 'Dashboard';
    }
    // Provider with a specific org selected → "{org name} Dashboard".
    // Operator impersonating a provider org uses operatorContext.orgName.
    if (activeOrgId) {
      const activeOrg = userOrgs?.find((o) => o.org_id === activeOrgId);
      if (activeOrg) return `${activeOrg.name} Dashboard`;
    }
    if (isOperatorInContext && operatorContext?.orgName) {
      return `${operatorContext.orgName} Dashboard`;
    }
    return user?.tenant_name ? `${user.tenant_name} Dashboard` : 'Dashboard';
  })();

  return (
    <DashboardTransition viewKey="solo">
    <WelcomeModal />
    <div className="min-h-screen bg-gray-200 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-baseline justify-between mb-4 gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2" data-testid="dashboard-title">{dashboardTitle}</h1>
            <p className="text-sm md:text-base text-gray-600">Welcome to the Claims Management System</p>
          </div>
          <div
            ref={companyDropdownRef}
            className="relative flex items-center gap-2 text-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
            data-testid="active-companies-header"
            onClick={() => setCompanyModalOpen(!companyModalOpen)}
            onMouseLeave={() => { if (companyModalOpen) companyCloseTimer.current = setTimeout(() => setCompanyModalOpen(false), 1500); }}
            onMouseEnter={() => { if (companyCloseTimer.current) clearTimeout(companyCloseTimer.current); }}
          >
            <Building2 className="w-5 h-5 text-sky-400" />
            <span className="text-sm font-bold text-gray-600">Active Companies</span>
            <span className="text-2xl font-bold">{stats?.companies ?? 0}</span>
            {companyModalOpen && companyList.length > 0 && (
              <div className="absolute right-0 top-full mt-2 z-30 w-64 bg-gray-200 rounded-lg shadow-lg" onClick={e => e.stopPropagation()} data-testid="company-list-modal">
                <div className="max-h-52 overflow-y-auto py-1">
                  {companyList.map((c, i) => (
                    <button
                      key={c.name || c.id || i}
                      onClick={() => {
                        setSelectedCompanyDashboard(c.name);
                        setCompanyModalOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 transition-colors flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
                      data-testid={`company-item-${i}`}
                    >
                      <Building2 className="w-3.5 h-3.5 text-sky-400 flex-shrink-0" />
                      <span>{c.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Date Filter */}
        <div className="mb-4 md:mb-6 overflow-x-auto" ref={dropdownRef}
          onMouseLeave={() => { if (dropdownOpen) dateCloseTimer.current = setTimeout(() => setDropdownOpen(false), 1500); }}
          onMouseEnter={() => { if (dateCloseTimer.current) clearTimeout(dateCloseTimer.current); }}
        >
          <div className="flex items-center gap-1 flex-wrap min-w-0">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 transition-colors"
              data-testid="date-filter-trigger"
            >
              <Calendar className="w-4 h-4 text-gray-400" />
              <span>{DATE_PRESETS.find(p => p.value === datePreset)?.label || 'All Time'}</span>
              {datePreset === 'custom' && customStart && !dropdownOpen && (
                <span className="text-gray-400 text-xs">({customStart.slice(5,7)}/{customStart.slice(8,10)}/{customStart.slice(0,4)} - {customEnd.slice(5,7)}/{customEnd.slice(8,10)}/{customEnd.slice(0,4)})</span>
              )}
              <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
            </button>
            {dropdownOpen && (
              <>
                <div className="w-px h-5 bg-gray-300 mx-1" />
                {DATE_PRESETS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => {
                      if (p.value !== 'custom') setPrevPreset(p.value);
                      if (p.value === 'custom') setPrevPreset(datePreset);
                      setDatePreset(p.value);
                      if (p.value !== 'custom') { setCustomStart(''); setCustomEnd(''); setDropdownOpen(false); }
                    }}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-md text-sm transition-colors ${datePreset === p.value ? 'text-blue-600 font-medium bg-blue-50' : 'text-gray-700 hover:bg-gray-50'}`}
                    data-testid={`date-preset-${p.value}`}
                  >
                    {p.label}
                  </button>
                ))}
                {datePreset === 'custom' && (
                  <>
                    <div className="w-px h-5 bg-gray-300 mx-1" />
                    <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                      className="px-2 py-1 bg-transparent border-b border-gray-300 text-sm text-gray-700 outline-none focus:border-blue-500" data-testid="custom-start-date" />
                    <span className="text-xs text-gray-400">to</span>
                    <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                      className="px-2 py-1 bg-transparent border-b border-gray-300 text-sm text-gray-700 outline-none focus:border-blue-500" data-testid="custom-end-date" />
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-6 mb-6 md:mb-8">
          {cards.map((s, i) => (
              <Card key={s.title || i} className="border-none shadow-none bg-transparent" data-testid={`stat-card-${i}`}>
                <CardContent className="p-6 text-center">
                  <p className="text-sm font-bold text-gray-600 mb-1 whitespace-nowrap">{s.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{s.value}</p>
                </CardContent>
              </Card>
          ))}
        </div>

        <LossTypeBar stats={stats} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <DashboardPie
            title="All Company Entries"
            data={stats?.company_entries ?? []}
            colorArray={COMPANY_COLORS}
          />
          <DashboardPie
            title="All Entry Statuses"
            data={stats?.status_entries ?? []}
            colorMap={STATUS_COLORS_MAP}
          />
          <DashboardPie
            title="All Entries By Faults"
            data={stats?.fault_entries ?? []}
            colorMap={FAULT_COLORS_MAP}
          />
        </div>

      </div>
    </div>
    </DashboardTransition>
  );
};

export default Home;
